'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { GraduationCap, BookOpen, ArrowRight, Sparkles } from 'lucide-react';

interface Props {
  email: string;
  onClose: () => void;
}

export default function RoleModal({ email, onClose }: Props) {
  const router = useRouter();
  const [selected, setSelected] = useState<'prospective' | 'browser' | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const handleContinue = async () => {
    if (!selected) return;
    setIsNavigating(true);
    // BACKEND: POST /api/user/role { role: selected } — store user intent for personalization
    await new Promise((r) => setTimeout(r, 600));
    if (selected === 'prospective') {
      router.push('/onboarding');
    } else {
      router.push('/student-portal');
    }
  };

  const firstName = email.split('@')[0].split('.')[0];
  const displayName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="gradient-navy px-8 py-7 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 opacity-10"
            style={{ background: 'radial-gradient(circle, rgb(96,165,250) 0%, transparent 70%)' }}
          />
          <div className="relative z-10">
            <div className="w-10 h-10 bg-blue-400/20 border border-blue-300/40 rounded-xl flex items-center justify-center mb-4">
              <Sparkles size={20} className="text-blue-300" />
            </div>
            <h2 className="font-display text-2xl font-semibold text-white mb-1">
              Welcome, {displayName}!
            </h2>
            <p className="text-white/70 text-sm">
              Help us personalize your IvyDirectory experience
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-7">
          <p className="text-sm text-[#5a6e96] mb-6 font-medium">
            How are you planning to use IvyDirectory today?
          </p>

          <div className="space-y-3 mb-8">
            {/* Prospective Student */}
            <button
              onClick={() => setSelected('prospective')}
              className={`w-full p-5 rounded-xl border-2 text-left transition-all duration-200 group ${
                selected === 'prospective' ? 'border-navy bg-navy/4 shadow-md' : 'border-blue-100 hover:border-navy/40 hover:bg-blue-50/50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                  selected === 'prospective' ? 'bg-navy text-white' : 'bg-navy/8 text-navy group-hover:bg-navy/15'
                }`}>
                  <GraduationCap size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-navy text-sm">Prospective Student</h3>
                    {selected === 'prospective' && (
                      <div className="w-5 h-5 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-[#5a6e96] text-xs mt-1 leading-relaxed">
                    I'm applying to Ivy League schools. I want admission stats, AI application evaluation, and personalized match scores.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {['AI Match Score', 'SAT Analysis', 'Admission Odds', 'Application Tracker'].map((tag) => (
                      <span key={`tag-${tag}`} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-md font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>

            {/* Just Browsing */}
            <button
              onClick={() => setSelected('browser')}
              className={`w-full p-5 rounded-xl border-2 text-left transition-all duration-200 group ${
                selected === 'browser' ? 'border-navy bg-navy/4 shadow-md' : 'border-blue-100 hover:border-navy/40 hover:bg-blue-50/50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                  selected === 'browser' ? 'bg-navy text-white' : 'bg-navy/8 text-navy group-hover:bg-navy/15'
                }`}>
                  <BookOpen size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-navy text-sm">Just Browsing</h3>
                    {selected === 'browser' && (
                      <div className="w-5 h-5 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-[#5a6e96] text-xs mt-1 leading-relaxed">
                    I'm curious about Ivy League history, notable alumni, past presidents, and Nobel laureates.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {['Alumni Profiles', 'Nobel Laureates', 'College History', 'Presidents'].map((tag) => (
                      <span key={`tag-browse-${tag}`} className="px-2 py-0.5 bg-blue-50 text-[#5a6e96] text-xs rounded-md font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Continue button */}
          <button
            onClick={handleContinue}
            disabled={!selected || isNavigating}
            className="w-full py-3.5 bg-navy text-white rounded-xl font-medium text-sm hover:bg-navy-light disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2 shadow-md"
          >
            {isNavigating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Setting up your experience...
              </>
            ) : (
              <>
                Continue to IvyDirectory
                <ArrowRight size={16} />
              </>
            )}
          </button>

          <p className="text-center text-xs text-[#5a6e96] mt-4">
            You can change this anytime in your profile settings
          </p>
        </div>
      </div>
    </div>
  );
}